import { GetUserResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseDeleteCompanyUserPhoneActionProps = {
    /**
     * Which of the member's two numbers this call is for — `primary` is
     * `phone`, `secondary` is `phone2`. The two endpoints are the same call
     * against a different field, so the slot is fixed for the hook's lifetime
     * rather than chosen per call.
     */
    slot: 'primary' | 'secondary';
    /**
     * What to say when the number is removed, as a whole sentence — "Secondary
     * phone deleted" rather than "Phone deleted". Spanish agrees the participle
     * with the noun, so this can never be assembled from a template. Defaults
     * to the generic.
     */
    successMessage?: string;
};

type UseDeleteCompanyUserPhoneActionData = {
    deletePhone: (companyUserId: string) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Removing the phone number stored against a member.
 *
 * Carries no `isAllowed` of its own: whether the viewer may edit this member is
 * `useEditProfileAction`, which gates the whole page of sections.
 */
export function useDeleteCompanyUserPhoneAction(
    props: UseDeleteCompanyUserPhoneActionProps
): UseDeleteCompanyUserPhoneActionData {
    const { slot, successMessage } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `DELETE /v3/users/{id}/phone` and `DELETE /v3/users/{id}/phone2` declare
    // gets a branch — the two share one error union.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (companyUserId: string) =>
            (slot === 'secondary'
                ? openApiRef.current.users.deleteUserPhone2(companyUserId)
                : openApiRef.current.users.deleteUserPhone(companyUserId)
            ).then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateCompanyUsersCache(queryClient, data.data.user);

            UIToast.success(successMessage ?? t('shared.toasts.phone_deleted'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        deletePhone: (companyUserId: string): Promise<GetUserResponse> =>
            mutation.mutateAsync(companyUserId),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
